import type { RouteLocationNormalizedLoaded } from "vue-router";

/**
 * F2 (perf audit 2026-07-02): shared key for route-driven remount boundaries
 * (the keyed <router-view> in templates/common/layout and the page-transition
 * frame in templates/layouts/default).
 *
 * Catalog routes handle query-string changes (?filters / ?page / ?sort /
 * ?search) IN PLACE via their own `route.fullPath` watcher in
 * pages/store/category/category.vue. Keying remount boundaries with the full
 * path forced a teardown of the whole page tree on every filter or page
 * click: async setup re-ran (suspense loader flash), instance state was lost
 * (loadedCategoryId, prefetch coordination), the grid was rebuilt and
 * ~80–100 product images re-requested.
 *
 * Catalog routes therefore key by PATH only:
 *  - "/store/category/:id" — direct category URLs. Navigating between two
 *    categories still changes the path and remounts.
 *  - "_slug" keyword URLs (/Glasses, /Fendi, …) — the seo-resolver renders
 *    category / product / manufacturer pages for these. Query-only
 *    navigation happens exclusively on catalog keywords: the manufacturer
 *    template has no query pagination and product pages never navigate
 *    query-only.
 *
 * Every other route keeps the query-sensitive full-path key: those pages
 * (manufacturer, special, blog category, …) read route.query during setup
 * only and rely on the remount to react to query changes.
 */
export const catalogAwareRouteKey = (
  route: RouteLocationNormalizedLoaded
): string => {
  const isCatalogRoute =
    route.name === "_slug" || String(route.path).startsWith("/store/category/");
  return isCatalogRoute ? route.path : route.fullPath;
};

export default catalogAwareRouteKey;
